import { memo, useCallback, useMemo } from 'react';

import { Menu } from '@headlessui/react';
import LogoutIcon from '@material-ui/icons/PowerSettingsNewRounded';
import { useDefaultBrand, useMyAccess } from 'data-layer';
import { LogoPlaceholder, QuestionMarkRounded } from 'icons';
import { FormattedMessage } from 'localization';
import { twMerge } from 'tailwind-merge';
import { DropdownItem, DropdownMenuButton } from 'ui';
import { AlertContainer, Heading } from 'ui/atoms';
import { useGetAdminMenuItems } from 'ui/organisms';
import { SettingsSidebarMenuItem } from 'ui/src/organisms/AdminSettingsSidebar/AdminSettingsSidebar';

type SettingsMenuProps = {
  navigate: any;
  legacyAppMode?: boolean;
};

export const SettingsMenu = memo(({ navigate, legacyAppMode = false }: SettingsMenuProps) => {
  const { data } = useDefaultBrand();
  const { menuItems, isLoading } = useGetAdminMenuItems();
  const { appModules } = useMyAccess();

  const allMenuItems = useMemo(() => {
    // Gate the "Help center / support" entry by the `support` module access
    // (fail-open while access is unknown). Sign-out is always shown.
    const canSupport = !appModules || appModules.includes('support');
    const extras = additionalItems.filter(
      (item) => item.translationKey !== 'userSupport.helpCenter' || canSupport,
    );
    return [...menuItems, ...extras];
  }, [menuItems, appModules]);

  const navigateTo = useCallback(
    (item: SettingsSidebarMenuItem) => {
      let target = '';
      if (legacyAppMode) {
        if (item.oldApp) {
          target = (item?.link ? item.link : item?.data?.[0]?.value) || '';
        } else {
          target = (item?.link ? '/v2' + item.link : '/v2' + item?.data?.[0]?.value) || '';
        }
      } else {
        target = (item?.link ? item.link : item?.data?.[0]?.value) || '';
      }

      const shouldRedirect = legacyAppMode ? !item.oldApp : item.oldApp;

      if (shouldRedirect) {
        window.location.href = target;
      } else {
        navigate(target);
      }
    },
    [navigate, legacyAppMode],
  );

  return (
    <>
      <DropdownMenuButton
        label={
          <div className="w-9 h-9 rounded-full text-v2blueGray-200 border border-v2blueGray-200 overflow-hidden cursor-pointer">
            <img
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                (e.target as HTMLImageElement).src = LogoPlaceholder;
              }}
              loading="lazy"
              src={data?.logo || LogoPlaceholder}
              className="w-9 h-9 object-contain"
            />
          </div>
        }
        noOverflowForPopup
        classNamePopup="border-none !rounded-none !bg-transparent !shadow-none p-0 !z-[3] w-[280px]"
        className="!z-[11]"
        position="bottom-end"
        buttonWrapperClassname="hover:bg-white bg-white"
      >
        <>
          {!isLoading && (
            <AlertContainer color="white" className="p-0 w-[280px] overflow-hidden">
              <>
                <div className="px-8 pt-6 pb-4 border-b border-b-gray-200">
                  <Heading>
                    <FormattedMessage id="conversationsHub.settings" />
                  </Heading>
                </div>

                <div className={'px-2 flex flex-col max-h-[80vh] overflow-y-auto relative'}>
                  <div className="pointer-events-none sticky top-0 left-0 w-full min-h-[8px] h-2 bg-gradient-to-b from-white to-transparent z-[2]" />

                  {allMenuItems?.map((x, index) => {
                    const isGenearalLast = index === allMenuItems.length - 1;
                    const isMenuLast = index === menuItems.length - 1;

                    const isSelected = x?.data?.some((x) => location?.pathname?.includes(x.value));

                    const sectionName = x?.translationKey ? (
                      <FormattedMessage id={x?.translationKey} />
                    ) : (
                      <>{x.title}</>
                    );

                    return (
                      <div key={`menu-settings-item-${x.translationKey}-${index}`}>
                        <Menu.Item>
                          <DropdownItem
                            onClick={() => navigateTo(x)}
                            wrapperClassName={twMerge('[&:not(:last-of-type)]:mb-[0px]')}
                            className={twMerge(
                              'whitespace-nowrap gap-[6px] capitalize mb-0',
                              !isGenearalLast && !x.hasDivider && !isMenuLast && 'mb-2',
                            )}
                            selected={isSelected}
                            icon={
                              x.icon && (
                                <span className={!isSelected ? 'text-v2blueGray-550' : ''}>
                                  {x.icon}
                                </span>
                              )
                            }
                          >
                            {sectionName}
                          </DropdownItem>
                        </Menu.Item>

                        {(x?.hasDivider || isMenuLast) && (
                          <div className="border-b border-v2blueGray-200 pb-2 mb-2" />
                        )}
                      </div>
                    );
                  })}

                  <div className="pointer-events-none sticky bottom-0 w-full min-h-[8px] h-2 bg-gradient-to-b from-transparent to-white z-[2]" />
                </div>
              </>
            </AlertContainer>
          )}
        </>
      </DropdownMenuButton>
    </>
  );
});

const additionalItems: SettingsSidebarMenuItem[] = [
  {
    translationKey: 'userSupport.helpCenter',
    icon: <QuestionMarkRounded width={24} height={24} />,
    data: [{ label: <></>, value: '/user-support' }],
    oldApp: false,
  },
  {
    // Logout now lives in the new app at /v2/auth/logout, so it is NOT an old-app
    // link: in the new app navigate() keeps it in-app (basename adds /v2), and in
    // the legacy app it is sent to '/v2/auth/logout'.
    translationKey: 'general.sign-out',
    icon: <LogoutIcon color="inherit" />,
    data: [{ label: <></>, value: '/auth/logout' }],
  },
];
